export const BILLING_ACCESS_DENIED_CODE = 'BILLING_ACCESS_DENIED';

export interface BillingAccessErrorPayload {
  code: string;
  reason?: string;
  status?: string;
  action?: string;
}

export interface BillingAccessMessage {
  title: string;
  body: string;
  ctaLabel: string;
}

const BILLING_ACCESS_PREFIX = `${BILLING_ACCESS_DENIED_CODE}|`;
const BILLING_ACCESS_REDIRECT_PATH = '/billing-access';
let lastBillingRedirectAt = 0;

export function parseBillingAccessErrorMessage(message: unknown): BillingAccessErrorPayload | null {
  if (typeof message !== 'string' || !message.startsWith(BILLING_ACCESS_PREFIX)) {
    return null;
  }

  const encodedPayload = message.slice(BILLING_ACCESS_PREFIX.length).trim();
  if (!encodedPayload) {
    return null;
  }

  try {
    const parsed = JSON.parse(encodedPayload) as BillingAccessErrorPayload;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      code: typeof parsed.code === 'string' ? parsed.code : BILLING_ACCESS_DENIED_CODE,
      reason: typeof parsed.reason === 'string' ? parsed.reason : undefined,
      status: typeof parsed.status === 'string' ? parsed.status : undefined,
      action: typeof parsed.action === 'string' ? parsed.action : undefined,
    };
  } catch {
    return null;
  }
}

export function parseBillingAccessError(error: unknown): BillingAccessErrorPayload | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const candidateMessages: unknown[] = [];
  const maybeError = error as {
    message?: unknown;
    graphQLErrors?: Array<{
      message?: unknown;
      extensions?: { originalError?: { errorMessage?: unknown } };
    }>;
  };

  candidateMessages.push(maybeError.message);

  for (const graphQLError of maybeError.graphQLErrors || []) {
    candidateMessages.push(graphQLError?.message);
    candidateMessages.push(graphQLError?.extensions?.originalError?.errorMessage);
  }

  for (const candidateMessage of candidateMessages) {
    const payload = parseBillingAccessErrorMessage(candidateMessage);
    if (payload) {
      return payload;
    }
  }

  return null;
}

export function getBillingAccessMessage(reason?: string): BillingAccessMessage {
  if (reason === 'trial_expired') {
    return {
      title: 'Your trial has ended',
      body: 'Renew your plan to keep building itineraries and continue where you left off.',
      ctaLabel: 'Renew plan',
    };
  }

  if (reason === 'past_due_write_blocked' || reason === 'unpaid_blocked' || reason === 'canceled_blocked') {
    return {
      title: 'Billing needs attention',
      body: 'Update your billing details to restore full access immediately.',
      ctaLabel: 'Fix billing',
    };
  }

  return {
    title: 'Subscription required',
    body: 'Your account billing status needs an update before you can continue.',
    ctaLabel: 'Manage subscription',
  };
}

function buildBillingAccessPath(payload: BillingAccessErrorPayload): string {
  const params = new URLSearchParams();

  if (payload.reason) {
    params.set('reason', payload.reason);
  }

  if (payload.status) {
    params.set('status', payload.status);
  }

  if (payload.action) {
    params.set('action', payload.action);
  }

  if (typeof window !== 'undefined') {
    params.set('next', `${window.location.pathname}${window.location.search}`);
  }

  const queryString = params.toString();
  return queryString ? `${BILLING_ACCESS_REDIRECT_PATH}?${queryString}` : BILLING_ACCESS_REDIRECT_PATH;
}

export function redirectToBillingAccess(payload: BillingAccessErrorPayload, now = Date.now()): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.location.pathname === BILLING_ACCESS_REDIRECT_PATH || window.location.pathname === '/billing') {
    return false;
  }

  if (now - lastBillingRedirectAt < 1200) {
    return false;
  }

  lastBillingRedirectAt = now;
  window.location.assign(buildBillingAccessPath(payload));
  return true;
}

export function __resetBillingAccessRedirectStateForTests() {
  lastBillingRedirectAt = 0;
}
